import { useState, useEffect } from 'react'
import { Plus, Trash2, Edit, Power, PowerOff, AlertTriangle, BarChart2 } from 'lucide-react'
import { Button, Modal, Card, Badge } from '.'
import { FilterBuilder } from './FilterBuilder'
import { filtersApi } from '@/api'
import type { InboundFilter, FilterCondition, FilterAction } from '@/types'
import toast from 'react-hot-toast'

interface FiltersListProps {
  appId: string
}

export function FiltersList({ appId }: FiltersListProps) {
  const [filters, setFilters] = useState<InboundFilter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingFilter, setEditingFilter] = useState<InboundFilter | null>(null)

  const loadFilters = async () => {
    try {
      const response = await filtersApi.listFilters(appId)
      setFilters(response.filters)
    } catch (error) {
      console.error('Failed to load filters:', error)
      toast.error('Failed to load filters')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId])

  const handleToggleActive = async (filter: InboundFilter) => {
    try {
      const updated = await filtersApi.updateFilter(appId, filter.id, {
        is_active: !filter.is_active,
      })
      setFilters(filters.map(f => (f.id === filter.id ? updated : f)))
      toast.success(updated.is_active ? 'Filter enabled' : 'Filter disabled')
    } catch {
      toast.error('Failed to update filter')
    }
  }

  const handleDelete = async (filter: InboundFilter) => {
    if (!confirm(`Delete filter "${filter.name}"? This cannot be undone.`)) {
      return
    }
    try {
      await filtersApi.deleteFilter(appId, filter.id)
      setFilters(filters.filter(f => f.id !== filter.id))
      toast.success('Filter deleted')
    } catch {
      toast.error('Failed to delete filter')
    }
  }

  const handleSave = async (data: {
    name: string
    description?: string
    action: FilterAction
    sample_rate?: number
    conditions: FilterCondition[]
    priority: number
    is_active: boolean
  }) => {
    try {
      if (editingFilter) {
        const updated = await filtersApi.updateFilter(appId, editingFilter.id, data)
        setFilters(filters.map(f => (f.id === editingFilter.id ? updated : f)))
        toast.success('Filter updated')
      } else {
        const created = await filtersApi.createFilter(appId, data)
        setFilters([...filters, created])
        toast.success('Filter created')
      }
      setShowCreateModal(false)
      setEditingFilter(null)
    } catch {
      toast.error('Failed to save filter')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-slate-200 rounded animate-pulse w-48" />
        <div className="h-24 bg-slate-200 rounded animate-pulse" />
        <div className="h-24 bg-slate-200 rounded animate-pulse" />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Inbound Filters</h3>
            <p className="text-sm text-slate-500">
              Drop or sample errors before they count against your quota
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Filter
          </Button>
        </div>

        {filters.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">No filters configured</p>
            <p className="text-sm text-slate-500 mb-4">
              Create filters to automatically drop or sample noisy errors
            </p>
            <Button variant="outline" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Filter
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {filters.map(filter => (
              <Card key={filter.id} className={`p-4 ${!filter.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-900">{filter.name}</h4>
                      <Badge variant={filter.is_active ? 'success' : 'warning'}>
                        {filter.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant={filter.action === 'drop' ? 'error' : 'primary'}>
                        {filter.action === 'drop'
                          ? 'Drop'
                          : `Sample ${(filter.sample_rate || 0) * 100}%`}
                      </Badge>
                    </div>
                    {filter.description && (
                      <p className="text-sm text-slate-500 mb-2">{filter.description}</p>
                    )}
                    <div className="text-xs text-slate-500 space-y-1">
                      <div className="flex flex-wrap gap-2">
                        {filter.conditions.map((c, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-100 rounded">
                            {c.field} {c.operator} {c.value || ''}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1">
                          <BarChart2 className="w-3 h-3" />
                          Matched: {filter.matched_count.toLocaleString()}
                        </span>
                        <span>Filtered: {filter.filtered_count.toLocaleString()}</span>
                        {filter.last_matched_at && (
                          <span>
                            Last match: {new Date(filter.last_matched_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(filter)}
                      className={`p-2 rounded-lg transition-colors ${
                        filter.is_active
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-slate-400 hover:bg-slate-100'
                      }`}
                      title={filter.is_active ? 'Disable filter' : 'Enable filter'}
                    >
                      {filter.is_active ? (
                        <Power className="w-4 h-4" />
                      ) : (
                        <PowerOff className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setEditingFilter(filter)
                        setShowCreateModal(true)
                      }}
                      className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      title="Edit filter"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(filter)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Delete filter"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <FilterModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setEditingFilter(null)
        }}
        filter={editingFilter}
        onSave={handleSave}
      />
    </>
  )
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filter: InboundFilter | null
  onSave: (data: {
    name: string
    description?: string
    action: FilterAction
    sample_rate?: number
    conditions: FilterCondition[]
    priority: number
    is_active: boolean
  }) => Promise<void>
}

function FilterModal({ isOpen, onClose, filter, onSave }: FilterModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [action, setAction] = useState<FilterAction>('drop')
  const [sampleRate, setSampleRate] = useState(10)
  const [conditions, setConditions] = useState<FilterCondition[]>([
    { field: 'message', operator: 'contains', value: '' },
  ])
  const [priority, setPriority] = useState(100)
  const [isActive, setIsActive] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (filter) {
      setName(filter.name)
      setDescription(filter.description || '')
      setAction(filter.action)
      setSampleRate((filter.sample_rate || 0.1) * 100)
      setConditions(filter.conditions)
      setPriority(filter.priority)
      setIsActive(filter.is_active)
    } else {
      setName('')
      setDescription('')
      setAction('drop')
      setSampleRate(10)
      setConditions([{ field: 'message', operator: 'contains', value: '' }])
      setPriority(100)
      setIsActive(true)
    }
  }, [filter, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    if (conditions.length === 0) {
      toast.error('At least one condition is required')
      return
    }

    setIsSaving(true)
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        action,
        sample_rate: action === 'sample' ? sampleRate / 100 : undefined,
        conditions,
        priority,
        is_active: isActive,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={filter ? 'Edit Filter' : 'Create Filter'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Filter Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Drop debug logs"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What does this filter do?"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">Conditions</label>
          <FilterBuilder conditions={conditions} onChange={setConditions} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
            <select
              value={action}
              onChange={e => setAction(e.target.value as FilterAction)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="drop">Drop (discard completely)</option>
              <option value="sample">Sample (keep percentage)</option>
            </select>
          </div>

          {action === 'sample' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sample Rate</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={sampleRate}
                  onChange={e =>
                    setSampleRate(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))
                  }
                  min={1}
                  max={100}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-slate-500">%</span>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
          <input
            type="number"
            value={priority}
            onChange={e =>
              setPriority(Math.max(0, Math.min(1000, parseInt(e.target.value) || 100)))
            }
            min={0}
            max={1000}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-slate-500 mt-1">Lower priority = evaluated first (0-1000)</p>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-slate-700">Enable filter immediately</span>
        </label>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : filter ? 'Update Filter' : 'Create Filter'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
